// src/pages/admin/Dashboard.js
import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, AppBar, Toolbar, Typography, IconButton,
  useMediaQuery, useTheme, Button, Snackbar, Alert, Tooltip
} from '@mui/material';
import MenuIcon   from '@mui/icons-material/Menu';
import LogoutIcon from '@mui/icons-material/Logout';
import { Routes, Route, useNavigate } from 'react-router-dom';

import Sidebar, { DRAWER_WIDTH_OPEN, DRAWER_WIDTH_COLLAPSED } from './components/Sidebar';
import Content          from './components/Content';
import HomepageRequests from './components/HomepageRequests';
import HomePageReqEdit  from './components/HomepageRequestsEdit';

const WARN_BEFORE_MS = 10 * 60 * 1000;

function getTokenExpiry() {
  try {
    const token = localStorage.getItem('token');
    if (!token) return null;
    return JSON.parse(atob(token.split('.')[1])).exp * 1000;
  } catch {
    return null;
  }
}

function Dashboard() {
  const theme    = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const navigate = useNavigate();

  const [sidebarOpen, setSidebarOpen] = useState(!isMobile);
  const [warnOpen,    setWarnOpen]    = useState(false);

  const handleLogout = useCallback(() => {
    localStorage.removeItem('token');
    navigate('/');
  }, [navigate]);

  // Token-Ablauf-Warnung
  useEffect(() => {
    const expiry = getTokenExpiry();
    if (!expiry) return;
    const msLeft = expiry - Date.now();
    if (msLeft <= 0) { handleLogout(); return; }
    if (msLeft <= WARN_BEFORE_MS) setWarnOpen(true);
    const warnDelay   = msLeft - WARN_BEFORE_MS;
    const warnTimer   = warnDelay > 0 ? setTimeout(() => setWarnOpen(true), warnDelay) : null;
    const logoutTimer = setTimeout(handleLogout, msLeft);
    return () => { if (warnTimer) clearTimeout(warnTimer); clearTimeout(logoutTimer); };
  }, [handleLogout]);

  const toggleSidebar = () => setSidebarOpen(p => !p);

  // Sidebar-Breite für den Content-Offset
  const sidebarWidth = sidebarOpen
    ? DRAWER_WIDTH_OPEN
    : (isMobile ? 0 : DRAWER_WIDTH_COLLAPSED);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>

      {/* ── AppBar: fix oben, volle Breite, ÜBER allem ── */}
      <AppBar
        position="fixed"
        sx={{ zIndex: (theme) => theme.zIndex.drawer + 1 }}
      >
        <Toolbar>
          <IconButton
            color="inherit"
            edge="start"
            onClick={toggleSidebar}
            sx={{ mr: 2 }}
            aria-label="Menü umschalten"
          >
            <MenuIcon />
          </IconButton>

          <Typography variant="h6" noWrap sx={{ flexGrow: 1 }}>
            Google Review Manager
          </Typography>

          <Tooltip title="Abmelden">
            <Button
              color="inherit"
              startIcon={<LogoutIcon />}
              onClick={handleLogout}
              size="small"
            >
              {isMobile ? '' : 'Abmelden'}
            </Button>
          </Tooltip>
        </Toolbar>
      </AppBar>

      {/* ── Bereich unter der AppBar: Sidebar + Content nebeneinander ── */}
      <Box sx={{ display: 'flex', flexGrow: 1, mt: '64px' /* AppBar-Höhe */ }}>

        {/* Sidebar (beginnt bei top:64px, siehe Sidebar.js) */}
        <Sidebar open={sidebarOpen} onToggle={toggleSidebar} />

        {/* Content: nimmt den restlichen Platz ein */}
        <Box
          component="main"
          sx={{
            flexGrow: 1,
            p: 1,
            overflow: 'auto',
            minHeight: 'calc(100vh - 64px)',
            // marginLeft nicht nötig, da Sidebar permanent ist und Platz reserviert
          }}
        >
          <Routes>
            <Route path="/"                  element={<Content />} />
            <Route path="/homepage-requests" element={<HomepageRequests />} />
            <Route path="/homepage-req-edit" element={<HomePageReqEdit />} />
          </Routes>
        </Box>
      </Box>

      {/* ── Token-Ablauf-Warnung ── */}
      <Snackbar open={warnOpen} anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
        <Alert
          severity="warning"
          action={
            <Button color="inherit" size="small" onClick={handleLogout}>
              Jetzt abmelden
            </Button>
          }
        >
          Ihre Sitzung läuft in weniger als 10 Minuten ab.
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default Dashboard;